
import { Component, Vue, Watch } from 'vue-property-decorator';
import {
  UsersApi,
  User,
  Building,
  ValidationProblemDetails,
  Property,
  PerformoSettings,
  ValueListItem,
  SystemApi,
  Contact
} from 'performo-api';
import { mapGetters } from 'vuex';
import Location1Picker from '@/components/system/Location1Picker.vue';
import FloorPicker from '@/components/system/FloorPicker.vue';
import Location2Picker from '@/components/system/Location2Picker.vue';

@Component({
  components: {
    Location1Picker,
    FloorPicker,
    Location2Picker
  },
  computed: mapGetters({
    currentProperty: 'currentProperty',
    currentPropertyId: 'currentPropertyId',
    settings: 'settings'
  })
})
export default class EditMyProfile extends Vue {
  // user profile
  private api = new UsersApi(this.$store.getters.getApiConfiguration);
  private sys = new SystemApi(this.$store.getters.getApiConfiguration);
  private settings!: PerformoSettings;
  private user: User = { contact: {} };
  private currentProperty!: Property;
  private currentPropertyId!: string;
  private departments: ValueListItem[] = [];

  // used to pass picked contact location data into the location pickers.
  // these values are "sticky" in the location pickers,
  // so that they persist across the triggered api calls and loading.
  private intendedLocationData: Contact = {};

  validationProblems: ValidationProblemDetails = {};

  get currentUserName(): string {
    return this.user &&
      this.user.contact &&
      this.user.contact.firstName &&
      this.user.contact.lastName
      ? this.user.contact.firstName + ' ' + this.user.contact.lastName
      : this.$store.getters.userDisplayName;
  }
  get floorLabel(): string {
    return this.currentProperty.floorLabel
      ? this.currentProperty.floorLabel
      : 'Floor';
  }

  private async mounted() {
    this.user = await this.api.getMyProfile();
    await this.configureLocationData();
  }

  @Watch('currentPropertyId', { deep: true })
  private async onCurrentPropertyIdChanged() {
    this.intendedLocationData.propertyId = this.currentPropertyId;
    if (this.user.contact) {
      this.user.contact.campus = undefined;
      this.user.contact.location1 = undefined;
      this.user.contact.location2 = undefined;
      this.user.contact.floor = undefined;
      this.user.contact.departmentName = undefined;
    }
    if (
      this.settings.allowRequestersToChangeTheirProperty &&
      this.user.contact
    ) {
      this.user.contact.propertyId = this.currentPropertyId;
    }
    await this.getDepartments();
  }

  private async configureLocationData() {
    if (this.user && this.user.contact) {
      if (this.settings.allowRequestersToChangeTheirProperty) {
        this.intendedLocationData.propertyId = this.user.contact.propertyId;
      } else {
        this.intendedLocationData.propertyId = this.currentPropertyId;
      }

      if (
        this.intendedLocationData.propertyId &&
        this.intendedLocationData.propertyId.length > 0
      ) {
        if (this.settings.enableCampuses) {
          this.intendedLocationData.campus = this.user.contact.campus;
        }
        this.intendedLocationData.location1 = this.user.contact.location1;
        this.intendedLocationData.location2 = this.user.contact.location2;
        this.intendedLocationData.floor = this.user.contact.floor;
      } else {
        this.intendedLocationData.campus = undefined;
        this.intendedLocationData.location1 = undefined;
        this.intendedLocationData.location2 = undefined;
        this.intendedLocationData.floor = undefined;
        this.intendedLocationData.departmentName = undefined;
      }
    }

    await this.getDepartments();
  }

  private async getDepartments() {
    if (this.settings.allowRequestersToSetWorkorderDepartment) {
      const propertyId = this.settings.allowRequestersToChangeTheirProperty
        ? this.intendedLocationData.propertyId
        : this.currentPropertyId;

      if (propertyId) {
        this.departments = await this.sys.getValueListItems({
          propertyId: propertyId,
          name: 'Department'
        });
      }
    }
  }

  private handleSuccess(update: User) {
    if (update) {
      this.$router.push({ name: 'my-profile' });
    }
  }

  private async handleError(error: ValidationProblemDetails | Response) {
    if (!error) {
      return;
    }

    if (error instanceof Response) {
      error = await error.json();
    }

    this.validationProblems = error;
  }

  private localPropertyPickedHandler(pickedProperty: string) {
    if (pickedProperty === this.currentProperty.propertyId) {
      // This is a special case where the "local" property picker is the same as the currentPropertyId
      // We still need to update the other conditionally rendered location pickers
      // So we call onCurrentPropertyIdChanged which would normally only be called when the currentPropertyId changes
      this.onCurrentPropertyIdChanged();
    }
  }

  private buildingPickedHandler(pickedBuilding: Building) {
    if (this.settings.enableCampuses && this.user.contact) {
      this.user.contact.campus = pickedBuilding.campusName;
    }
  }

  // This function has been directly lifted from \components\orders\requester\create.vue
  get allowProfileEdit(): boolean {
    if (!this.settings.requireProfileForRequests) return true;
    if (!this.settings.requiredProfileAttributes) return true;
    // Check the contact has a value for every required attribute
    const result = this.settings.requiredProfileAttributes.every((attr) => {
      if (!this.user.contact) return false;
      // https://stackoverflow.com/questions/56833469/typescript-error-ts7053-element-implicitly-has-an-any-type/56833507#56833507
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const contact: { [index: string]: any } = this.user.contact;
      return contact[attr] ? true : false;
    });
    // Check the user's phone number is in the correct format
    if (this.user.contact && this.user.contact.phone) {
      const phonePattern = /^[0-9]{3}-[0-9]{3}-[0-9]{4}$/;
      return result && phonePattern.test(this.user.contact.phone);
    }

    return result;
  }

  private updateProfile() {
    if (!(this.user && this.user.contact)) {
      return;
    }
    if (!this.allowProfileEdit) {
      return;
    }
    this.api
      .putMyProfile({
        modifyProfileRequest: {
          loginName: this.user.contact.emailAddress || '',
          lastName: this.user.contact.lastName || '',
          firstName: this.user.contact.firstName || '',
          phone: this.user.contact.phone || '',
          company: this.user.contact.company || '',
          department: this.user.contact.departmentName || '',
          propertyId: this.user.contact.propertyId || '',
          campus: this.user.contact.campus || '',
          building: this.user.contact.location1 || '',
          floor: this.user.contact.floor || '',
          suite: this.user.contact.location2 || ''
        }
      })
      .then(this.handleSuccess)
      .catch(this.handleError);
  }
}
